import { CloseIcon, StarFilledIcon } from "@App/Components/common/Icons";
import type { MarkerProps } from "@App/Components/molecules/common/map/markers/MarkerProps";
import { useAssetUrl } from "@App/hooks/useAssetUrl";
import type { State } from "@App/state";
import {
  GarageIsBookable,
  GarageShouldDisplayMapMarkerRating,
} from "@App/utils/conditionals/garages";
import { formatShortDate } from "@App/utils/dates";
import { getConfig } from "@App/utils/getConfig";
import { Fragment, type FunctionComponent } from "react";
import { useSelector } from "react-redux";

export const DefaultMarker: FunctionComponent<MarkerProps> = ({
  garage,
  service,
  open,
  onClick,
  onToggle,
  Icon,
  content = "distance",
}: MarkerProps) => {
  const { url } = useAssetUrl();
  const organization = useSelector((state: State) => state.orders.organization);

  const displaysRating = GarageShouldDisplayMapMarkerRating(
    garage,
    service,
    getConfig(organization).reviews.show,
  );

  const garageRatingOutOfFive = (rating: number, fraction: number) => {
    return ((rating / 100) * 5).toFixed(fraction).replace(".", ",");
  };

  function getIconText(): string {
    if (content === "earliestSlot") {
      const hasAvailableEarliestSlot =
        garage.available_days?.length > 0 && garage.available_days[0].available_at;
      if (hasAvailableEarliestSlot) return formatShortDate(garage.available_days[0].available_at);
      return "";
    } else {
      return `${(garage.distance / 1000).toFixed(2)} km`;
    }
  }

  return (
    <div className={"absolute cursor-pointer transition-all duration-400"}>
      <div
        className={`z-10 cursor-auto shadow bg-white w-screen md:w-80 fixed md:absolute left-[-50%] md:left-0 bottom-[16vh] md:bottom-3 p-3 md:mb-5 ${open ? "show" : "hidden"}`}
      >
        <div className="grid gap-4 grid-cols-5">
          <div className={"col-span-2"}>
            {garage.cover_image ? (
              <img
                src={`${url}/storage/${garage.cover_image}`}
                className={"w-full h-auto mb-3"}
                alt=""
              />
            ) : (
              <div
                className={
                  "flex items-center justify-center text-center text-neutral-700 font-serif font-bold bg-neutral-300 w-full h-2/3 min-h-[90px] lg:min-h-min mb-2"
                }
              >
                {organization.name}
                <br />
                Service-Standort
              </div>
            )}
            {GarageIsBookable(garage) && (
              <button
                type={"button"}
                className={"bg-primary text-white px-4 py-2 w-full"}
                onClick={() => onClick(garage)}
              >
                Termin wählen
              </button>
            )}
          </div>
          <div className={"col-span-3"}>
            <div className="flex justify-between mb-3">
              <div className="text-xl">{garage.name}</div>
              <div onClick={() => onToggle(garage, false)}>
                <CloseIcon />
              </div>
            </div>
            {GarageIsBookable(garage) && (
              <Fragment>
                {displaysRating && (
                  <div className={"flex mb-3"}>
                    <StarFilledIcon />
                    <p>{garageRatingOutOfFive(garage.rating, 2)}</p>
                  </div>
                )}
                <div className={"font-bold"}>
                  Nächster Termin:{" "}
                  {(garage?.available_days?.length ?? 0) > 0
                    ? formatShortDate(garage?.available_days?.[0]?.available_at ?? "")
                    : "kA"}
                </div>
              </Fragment>
            )}
            {!GarageIsBookable(garage) && garage.street && (
              <div className={"font-bold"}>
                <p>
                  {garage.street}, {garage.postcode} {garage.city}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Icon
        onClick={() => onToggle(garage, true)}
        state={open ? "clicked" : "default"}
        rating={displaysRating ? garageRatingOutOfFive(garage.rating, 1) : null}
      >
        {getIconText()}
      </Icon>
    </div>
  );
};
